/**
 * User Menu Component
 * Displays user info and logout option
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import CliTokenModal from './CliTokenModal';


const UserMenu: React.FC = () => {
  const { user, logout } = useAuth();
  const { currentTenant, currentUser } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const [showTokenModal, setShowTokenModal] = useState(false);

  if (!user) return null;

  // Check if user is admin or owner (can publish workflows)
  const canPublishWorkflows = currentUser?.role === 'admin' || currentUser?.role === 'owner';

  const displayName = user.displayName || user.email || 'User';
  const avatarLetter = displayName.charAt(0).toUpperCase();

  const sectionHdr  = 'px-4 py-1.5 text-meta font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-100';
  const staticItem  = 'px-4 py-3';
  const actionItem  = 'w-full flex items-center gap-2.5 px-4 py-2.5 text-label text-gray-700 hover:bg-surface-subtle transition-colors duration-fast';

  return (
    <div className="relative">
      {/* Trigger */}
      <button type="button" onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2.5 px-3 py-2 rounded-md hover:bg-surface-subtle transition-colors duration-fast">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-label font-bold flex-shrink-0">
          {avatarLetter}
        </div>
        <div className="text-left hidden sm:block">
          <div className="text-label font-medium text-gray-900 leading-tight">{displayName}</div>
          {currentTenant && <div className="text-meta text-gray-500 leading-tight">{currentTenant.name}</div>}
        </div>
        <svg className={`text-gray-400 transition-transform duration-fast ${isOpen ? 'rotate-180' : ''}`} width="12" height="12" viewBox="0 0 12 12">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 top-full mt-2 z-50 w-64 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
            {/* Account */}
            <div className={sectionHdr}>Account</div>
            <div className={staticItem}>
              <div className="text-label font-semibold text-gray-900">{displayName}</div>
              <div className="text-meta text-gray-500 mt-0.5">{user.email}</div>
            </div>

            {/* Org */}
            {currentTenant && currentUser && (
              <>
                <div className={sectionHdr}>Organization</div>
                <div className={staticItem}>
                  <div className="text-label font-semibold text-gray-900">{currentTenant.name}</div>
                  <div className="text-meta text-gray-500 mt-0.5">Role: {currentUser.role}</div>
                  <div className="text-meta text-gray-500">Plan: {currentTenant.plan}</div>
                </div>
              </>
            )}

            {/* Developer */}
            {canPublishWorkflows && (
              <>
                <div className={sectionHdr}>Developer</div>
                <button type="button" className={actionItem} onClick={() => { setShowTokenModal(true); setIsOpen(false); }}>
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" className="text-gray-500 flex-shrink-0">
                    <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" />
                    <path d="M3.5 7.5h1v1h-1v-1zm8 0h1v1h-1v-1z" />
                  </svg>
                  Get CLI Token
                </button>
              </>
            )}

            {/* Sign out */}
            <div className="border-t border-gray-100">
              <button type="button" className={`${actionItem} text-error hover:bg-error-light`} onClick={logout}>
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="flex-shrink-0">
                  <path d="M11 1h3a1 1 0 011 1v12a1 1 0 01-1 1h-3M8 12l4-4-4-4M12 8H1" />
                </svg>
                Sign Out
              </button>
            </div>
          </div>
        </>
      )}

      <CliTokenModal isOpen={showTokenModal} onClose={() => setShowTokenModal(false)} />
    </div>
  );
};

export default UserMenu;
